import { skipToken } from "@reduxjs/toolkit/query";
import {
  getOutcomeNoun,
  isRevenueOutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import {
  getOutcomeDefinitionFromPlaybook,
  type AdminApiPlaybook,
  type PlaybookCriterionId,
  type PlaybookCriterionNode,
  type PlaybookNode,
  type PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import { Size } from "@repo/ui/helpers/helpers";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { useEffect, useMemo, type FC } from "react";
import { useNavigate } from "react-router";
import {
  usePlaybookCriterionCoverageSecondsQuery,
  usePlaybookGlobalAverageScorePerCriteriaQuery,
  usePlaybookHighImpactCriteriaQuery,
} from "../../../api/entities/performancePlaybook.js";
import { appPaths } from "../../../app/appPaths.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectCompany } from "../../company/companySlice.js";
import { buildTeamPerformancePageQueryArgs } from "../../filters/buildFilterQueryArgs.js";
import { useFilters } from "../../filters/filters.js";
import { CriteriaOutcomeCorrelationImpactPill } from "../criteriaOutcomeCorrelationImpactPill.js";
import { CriterionAverageScore } from "../criterionAverageScore.js";
import { CriterionTimeBar } from "../criterionTimeBar.js";
import { GradingPassRatePill } from "../gradingPassRatePill.js";
import { PlaybookCriteriaTreeTable } from "../playbookCriteriaTreeTable.js";
import { usePlaybookCriterionCoverageHelpers } from "../playbookCriterionCoverageHelpers.js";
import { countTrendDays } from "../trendChart.js";
import { computeTrendDelta, TrendDeltaLabel } from "../trendDeltaLabel.js";

export interface PlaybookCriteriaAggregatesTableProps {
  playbook: AdminApiPlaybook;
  selectedCriterionId: PlaybookCriterionId | undefined;
  onFetchingChange?: (isFetching: boolean) => void;
}

export const PlaybookCriteriaAggregatesTable: FC<
  PlaybookCriteriaAggregatesTableProps
> = (props) => {
  const navigate = useNavigate();

  const playbookId = props.playbook.id;
  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(props.playbook);
  const [{ teammateGroupIds }] = useFilters(["teammateGroupIds"]);

  const isCriteriaCoverageEnabled =
    useAppSelector(selectCompany)?.settings.criteria_coverage_enabled ?? false;

  const now = useNow();

  const [
    {
      dateRange,
      outcome,
      customFields,
      playbookConditions,
      signalId,
      signalMetadataFields,
      totalRecordingDuration,
      dateBasis,
    },
  ] = useFilters(
    [
      "dateRange",
      "outcome",
      "customFields",
      "playbookConditions",
      "signalId",
      "signalMetadataFields",
      "totalRecordingDuration",
      "dateBasis",
    ],
    { playbookId },
  );

  const trendDays = countTrendDays(dateRange);

  const queryArgs =
    teammateGroupIds !== undefined
      ? buildTeamPerformancePageQueryArgs(now, {
          playbookId,
          dateRange,
          teammateGroupIds,
          outcome,
          customFields,
          playbookConditions,
          signalId,
          signalMetadataFields,
          totalRecordingDuration,
          dateBasis,
        })
      : skipToken;

  // Get the global average score per criteria for the selected group (or whole company)
  const {
    data: globalAverageScorePerCriteria,
    isLoading: isLoadingScores,
    isFetching: isFetchingScores,
  } = usePlaybookGlobalAverageScorePerCriteriaQuery(queryArgs);

  // The query is skipped until the group filter loads from preferences, which
  // is still "no scores yet".
  const hasNoScoresYet = queryArgs === skipToken || isLoadingScores;

  // Get the high impact criteria details (rank + calibrated conversions)
  // for rendering the "high priority" pill on each criterion.
  const {
    data: highImpactCriteria,
    isLoading: isLoadingImpact,
    isFetching: isFetchingImpact,
  } = usePlaybookHighImpactCriteriaQuery(
    outcomeDefinition && teammateGroupIds !== undefined
      ? {
          playbookId,
          groupIds: teammateGroupIds,
          asOfDateIso: dateRange.end?.toISO() ?? now.endOf("day").toISO(),
        }
      : skipToken,
  );

  const highImpactDetailByCriteriaId = useMemo(() => {
    if (!highImpactCriteria) return undefined;
    return new Map(
      highImpactCriteria.high_impact_criteria
        .filter((entry) => entry.is_high_impact)
        .map((entry) => [entry.criteria_node_id, entry]),
    );
  }, [highImpactCriteria]);

  // Per-criterion average coverage seconds across all teammates in the
  // playbook (optionally narrowed to the selected teammate group), plus the
  // company top-10 benchmark for the same criteria. ClickHouse-only.
  const {
    data: criterionCoverageSeconds,
    isLoading: isLoadingCoverage,
    isFetching: isFetchingCoverage,
  } = usePlaybookCriterionCoverageSecondsQuery(queryArgs);

  const isFetchingTable =
    isFetchingScores || isFetchingImpact || isFetchingCoverage;

  const onFetchingChange = useEffectEvent(props.onFetchingChange ?? (() => {}));
  useEffect(() => {
    onFetchingChange(isFetchingTable);
  }, [isFetchingTable, onFetchingChange]);

  const {
    hasAverageCoverageSeconds,
    getNodeAverageCoverageSeconds,
    getNodeTopPerformerAverageCoverageSeconds,
    maxAverageCoverageSeconds,
  } = usePlaybookCriterionCoverageHelpers(criterionCoverageSeconds);

  // Get the noun form for outcome (e.g., "enrollment")
  const outcomeNoun = outcomeDefinition
    ? getOutcomeNoun(outcomeDefinition)
    : undefined;
  const isRevenueOutcome = outcomeDefinition
    ? isRevenueOutcomeDefinition(outcomeDefinition)
    : undefined;

  // When a criteria is selected, the left panel is narrower (collapsed)
  const isCollapsed = props.selectedCriterionId !== undefined;

  const renderCriterionImpactPill = (criterion: PlaybookCriterionNode) => {
    const detail = highImpactDetailByCriteriaId?.get(criterion.id);
    if (!detail || !highImpactCriteria) {
      return null;
    }

    return (
      <CriteriaOutcomeCorrelationImpactPill
        isCollapsed={isCollapsed}
        rank={detail.rank}
        totalCriteria={highImpactCriteria.total_criteria_count}
        netConversions={Math.round(detail.net_conversions)}
        averageRevenuePerOpp={
          highImpactCriteria.average_revenue_per_opp ?? undefined
        }
        outcomeNoun={outcomeNoun}
        outcomePluralNoun={outcomeDefinition?.plural_noun}
        isRevenueOutcome={isRevenueOutcome}
      />
    );
  };

  const renderCriterionScore = (criterion: PlaybookCriterionNode) => {
    const averageScore = globalAverageScorePerCriteria?.find(
      (item) => item.criteria_node_id === criterion.id,
    )?.global_average_score;

    const renderAverageScoreTrend = () => {
      if (averageScore === undefined) {
        return null;
      }

      const averageScoreTrend = globalAverageScorePerCriteria?.find(
        (item) => item.criteria_node_id === criterion.id,
      )?.global_average_score_sparkline;

      if (!averageScoreTrend) {
        return null;
      }

      const delta = computeTrendDelta(averageScoreTrend);
      if (delta === null) {
        return null;
      }

      return (
        <TrendDeltaLabel
          delta={delta}
          gradingValueType={criterion.grading_value_type}
          trendDays={trendDays}
          size={Size.SMALL}
        />
      );
    };

    return (
      <div className="flex items-center gap-3">
        <CriterionAverageScore
          valueType={criterion.grading_value_type}
          value={averageScore}
          isLoading={hasNoScoresYet}
          sizedToMax
        />
        {renderAverageScoreTrend()}
      </div>
    );
  };

  const renderSectionScore = (section: PlaybookSectionNode) => {
    const averageScore = globalAverageScorePerCriteria?.find(
      (item) => item.criteria_node_id === section.id,
    )?.global_average_score;

    const renderAverageScoreTrend = () => {
      if (averageScore === undefined) {
        return null;
      }

      const averageScoreTrend = globalAverageScorePerCriteria?.find(
        (item) => item.criteria_node_id === section.id,
      )?.global_average_score_sparkline;

      if (!averageScoreTrend) {
        return null;
      }

      const delta = computeTrendDelta(averageScoreTrend);
      if (delta === null) {
        return null;
      }

      return (
        <TrendDeltaLabel
          delta={delta}
          gradingValueType="yes_no"
          trendDays={trendDays}
          size={Size.SMALL}
        />
      );
    };

    return (
      <div className="flex items-center gap-3">
        <GradingPassRatePill
          size={Size.SMALL}
          passRate={averageScore}
          isLoading={hasNoScoresYet}
          sizedToMax
        />
        {renderAverageScoreTrend()}
      </div>
    );
  };

  const renderTimeCell = (node: PlaybookNode) => {
    const playbookAverageSeconds = getNodeAverageCoverageSeconds(node);
    if (playbookAverageSeconds === undefined) return null;
    return (
      <CriterionTimeBar
        teammateAverageSeconds={playbookAverageSeconds}
        topPerformerAverageSeconds={getNodeTopPerformerAverageCoverageSeconds(
          node,
        )}
        maxSeconds={maxAverageCoverageSeconds ?? playbookAverageSeconds}
      />
    );
  };

  return (
    <PlaybookCriteriaTreeTable
      assessmentTree={props.playbook.assessment_tree}
      selectedCriterionId={props.selectedCriterionId}
      isCompact={isCollapsed}
      // Hide the Time column when a criterion is selected (left panel is
      // narrower then) or when there's no coverage data to show.
      showsTimeColumn={
        !isCollapsed && hasAverageCoverageSeconds && isCriteriaCoverageEnabled
      }
      renderSection={(section) => ({
        score: renderSectionScore(section),
        // Disabled for now: time: renderTimeCell(section)
      })}
      renderCriterion={(criterion) => ({
        titleSuffix: renderCriterionImpactPill(criterion),
        score: renderCriterionScore(criterion),
        time: renderTimeCell(criterion),
        onPress: () =>
          navigate(
            appPaths.playbookCriterion.build({
              playbookId,
              criterionId: criterion.id,
            }),
          ),
      })}
    />
  );
};
